const KIRI_BASE_URL = 'https://api.kiriengine.app/api';
const API_KEY = import.meta.env?.VITE_KIRI_ENGINE_API_KEY;

function getHeaders() {
  return {
    Authorization: `Bearer ${API_KEY}`,
    'Content-Type': 'application/json',
  };
}

/**
 * Get the status of a 3D model task by serial number.
 * Returns: { code, msg, data: { serialNum, status, ... } }
 */
export async function getModelStatus(serialNum) {
  const res = await fetch(`${KIRI_BASE_URL}/v1/model3d/status?serialNum=${encodeURIComponent(serialNum)}`, {
    method: 'GET',
    headers: getHeaders(),
  });
  if (!res?.ok) {
    const err = await res?.json()?.catch(() => ({}));
    throw new Error(err?.msg || `Kiri Engine error: ${res.status}`);
  }
  return res?.json();
}

/**
 * Get the download link for a completed 3D model by serial number.
 * Returns: { code, msg, data: { serialNum, downloadUrl, ... } }
 */
export async function getModelDownloadLink(serialNum) {
  const res = await fetch(`${KIRI_BASE_URL}/v1/model3d/downloadLink?serialNum=${encodeURIComponent(serialNum)}`, {
    method: 'GET',
    headers: getHeaders(),
  });
  if (!res?.ok) {
    const err = await res?.json()?.catch(() => ({}));
    throw new Error(err?.msg || `Kiri Engine error: ${res.status}`);
  }
  return res?.json();
}

/**
 * Get account balance / credits.
 * Returns: { code, msg, data: { balance } }
 */
export async function getBalance() {
  const res = await fetch(`${KIRI_BASE_URL}/v1/balance`, {
    method: 'GET',
    headers: getHeaders(),
  });
  if (!res?.ok) {
    const err = await res?.json()?.catch(() => ({}));
    throw new Error(err?.msg || `Kiri Engine error: ${res.status}`);
  }
  return res?.json();
}

/**
 * Upload images for a Photo Scan task.
 * @param {File[]} imageFiles - Array of image File objects (20–300 images)
 * @param {object} params - { modelQuality, textureQuality, isMask, textureSmoothing, fileFormat }
 * Returns: { code, msg, data: { serialNum } }
 */
export async function uploadPhotoScan(imageFiles, params = {}) {
  const formData = new FormData();

  imageFiles?.forEach((file) => {
    formData?.append('files', file);
  });

  if (params?.modelQuality !== undefined) formData?.append('modelQuality', String(params?.modelQuality));
  if (params?.textureQuality !== undefined) formData?.append('textureQuality', String(params?.textureQuality));
  if (params?.isMask !== undefined) formData?.append('isMask', String(params?.isMask));
  if (params?.textureSmoothing !== undefined) formData?.append('textureSmoothing', String(params?.textureSmoothing));
  formData?.append('fileFormat', params?.fileFormat || 'glb');

  const res = await fetch(`${KIRI_BASE_URL}/v1/photoScan/upload`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${API_KEY}`,
      // Do NOT set Content-Type here — browser sets it with boundary for multipart
    },
    body: formData,
  });

  if (!res?.ok) {
    const err = await res?.json()?.catch(() => ({}));
    throw new Error(err?.msg || `Kiri Engine upload error: ${res.status}`);
  }
  return res?.json();
}

export const isConfigured = () => !!API_KEY && API_KEY !== 'your-kiri-engine-api-key-here';
